import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Input,
  input,
  OnDestroy,
  output,
  signal,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Nullable } from 'primeng/ts-helpers';
import { IconKey } from '../../../../../../whitelabel/icon-key.enum';
import { LanguageService } from '../../../../../core/services/language.service';
import { PanelComponent } from '../../../../../osp-ui/components/panel/panel/panel.component';
import { OspTemplateDirective } from '../../../../../osp-ui/directives/osp-template.directive';
import { GenericCreateGeofenceCommand } from '../../contract/generic-create-geofence-command.contract';
import { GenericGeofence } from '../../contract/generic-geofence.contract';
import { GeofenceListItemOption } from '../../contract/geofence-list-item-option.contract';
import { GeofenceListItem } from '../../contract/geofence-list-item.contract';
import { GeofenceBuilderService } from '../../services/geofence-builder.service';
import { CreateGeofenceFormComponent } from './components/create-geofence-form/create-geofence-form.component';
import { SidebarMode } from './enum/sidebar-mode.enum';

@Component({
  selector: 'osp-create-geofence-sidebar',
  standalone: true,
  imports: [
    PanelComponent,
    CreateGeofenceFormComponent,
    OspTemplateDirective,
    TranslateModule,

  ],
  templateUrl: './create-geofence-sidebar.component.html',
  styleUrl: './create-geofence-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateGeofenceSidebarComponent implements OnDestroy {

  private readonly languageService = inject(LanguageService);

  readonly IconKey = IconKey;
  private readonly defaultMapSizeDOMRect = <DOMRect>{width: 700, height: 700};
  private readonly dispoColor = '#000000';

  map = input<google.maps.Map | Nullable>(null);
  mapSize = input<DOMRect | Nullable>(this.defaultMapSizeDOMRect);
  loading = input<boolean>(false);
  createEnabled = input<boolean>(false);

  @Input() set geofences(items: GenericGeofence[] | Nullable) {
    this.removeAllGeofences();
    this.$geofenceOptions.set(this.convertToGeofenceListItems(items ?? []));
    this.showAllGeofences();
  }

  public onCreate = output<GenericCreateGeofenceCommand>();
  $geofenceOptions = signal<GeofenceListItemOption[]>([]);
  $selectedGeofenceId = signal<string | null>(null);
  $sidebarMode = signal<SidebarMode>(SidebarMode.CREATE);
  $isNoData = computed(() => this.$geofenceOptions().length <= 0);

  constructor() {
    effect(() => this.handleSidebarModeChange(), {allowSignalWrites: true});
  }

  ngOnDestroy(): void {
    this.removeAllGeofences();
  }

  showSingleGeofence(geofenceListItem: GeofenceListItem, move = true): void {
    this.hideAllGeofences();
    geofenceListItem.polygon.setVisible(true);

    const currentSelectedId = this.$selectedGeofenceId();
    if (currentSelectedId === geofenceListItem.id) {
      this.showAllGeofences();
    } else {
      this.$selectedGeofenceId.set(geofenceListItem.id);
    }

    if (move) {
      const bounds = new google.maps.LatLngBounds();
      geofenceListItem.polygon.getPath().forEach((coord: google.maps.LatLng) => bounds.extend(coord));
      if (this.map()) {
        this.map()!.panToBounds(bounds);
        this.map()!.fitBounds(bounds);
      }
    }
  }

  showAllGeofences(): void {
    this.$selectedGeofenceId.set(null);
    this.$geofenceOptions().forEach(({geofence}) => geofence.polygon.setVisible(true));
  }

  handleCreateModeEnterAction(): void {
    this.$sidebarMode.set(SidebarMode.CREATE);
  }

  handleCancelAction(): void {
    this.$sidebarMode.set(SidebarMode.CREATE);
  }

  handleCreateAction(cmd: GenericCreateGeofenceCommand): void {
    this.$sidebarMode.set(SidebarMode.DEFAULT);
    this.onCreate.emit(cmd);
  }

  private handleSidebarModeChange(): void {
    if (this.$sidebarMode() === SidebarMode.CREATE) {
      this.hideAllGeofences();
    } else {
      this.showAllGeofences();
    }
  }

  private convertToGeofenceListItems(geofences: GenericGeofence[]): GeofenceListItemOption[] {
    return geofences.map((el) => {
      return {
        label: el.name,
        value: el.id,
        geofence: el.isDispositionGeofence ? this.getGeofenceItem(el, 4, this.dispoColor) : this.getGeofenceItem(el),
      };
    });
  }

  private getGeofenceItem(item: GenericGeofence, strokeWeight?: number, strokeColor?: string): GeofenceListItem {
    return {
      ...item,
      polygon: GeofenceBuilderService.buildPolygon(
        this.map()!,
        item.coordinates,
        item.color,
        strokeWeight,
        strokeColor,
      ),
    };
  }

  private removeAllGeofences(): void {
    this.$geofenceOptions().forEach(({geofence}) => geofence.polygon.setMap(null));
  }

  private hideAllGeofences(): void {
    this.$geofenceOptions().forEach(({geofence}) => {
      geofence.polygon.setVisible(false);
    });
  }

  protected translate(key: string): string {
    return this.languageService.getInstant(key);
  }
}
